import { useCallback, useEffect, useRef, useState } from 'react'
import {
  computeCompleteness,
  getErrorMessage,
  loadStoredFilters,
  saveStoredFilters,
  toApiParams
} from './managementUtils'

/**
 * Shared state for Management MIS reports: persisted filters, loading, error,
 * last refresh time, completeness and the viewer's MIS permissions.
 */
export function useManagementReport(fetcher) {
  const [filters, setFiltersState] = useState(loadStoredFilters)
  const [data, setData] = useState(null)
  const [access, setAccess] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)
  const requestId = useRef(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const apiKey = JSON.stringify(toApiParams(filters))

  const load = useCallback(async ({ silent = false } = {}) => {
    const id = ++requestId.current
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetcherRef.current(JSON.parse(apiKey))
      if (id !== requestId.current) return
      if (res?.success) {
        setData(res.data)
        if (res.access) setAccess(res.access)
        setLastRefresh(new Date())
      } else {
        setData(null)
        setError(res?.message || 'Report returned no data')
      }
    } catch (err) {
      if (id !== requestId.current) return
      setData(null)
      setError(getErrorMessage(err))
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [apiKey])

  useEffect(() => { load() }, [load])

  const setFilters = useCallback((next) => {
    setFiltersState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next
      saveStoredFilters(value)
      return value
    })
  }, [])

  const permissions = access?.permissions || []
  const can = useCallback((perm) => Boolean(access?.isAdmin) || permissions.includes(perm), [access, permissions])

  return {
    filters,
    setFilters,
    data,
    access,
    can,
    loading,
    error,
    lastRefresh,
    completeness: data ? computeCompleteness(data) : null,
    reload: load
  }
}

export default useManagementReport
